// src/utils/theme.js
// ---------------------------------------------------------------------------
// Which theme is in force, and how to change it.
//
// THREE STATES, NOT TWO. A viewer can pick light, pick dark, or pick neither —
// and "neither" is the default, where the operating system decides. Storing only
// a boolean would lose that third state and force everyone onto whichever we
// guessed. So the stored value is 'light' | 'dark' | 'system', and only the
// first two stamp data-theme onto the root; 'system' deliberately stamps
// nothing and lets the CSS media query answer.
// ---------------------------------------------------------------------------

const KEY = 'decon.theme';
const ORDER = ['system', 'light', 'dark'];

export function readTheme() {
  try {
    const v = localStorage.getItem(KEY);
    return ORDER.includes(v) ? v : 'system';
  } catch { return 'system'; }
}

/** What is actually on screen right now, with 'system' resolved. */
export function effectiveTheme(pref = readTheme()) {
  if (pref !== 'system') return pref;
  try {
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  } catch { return 'dark'; }
}

export function applyTheme(pref) {
  const root = document.documentElement;
  if (pref === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', pref);
  // Tells the browser which scrollbars and form controls to draw. Without it
  // a light page keeps dark native widgets.
  root.style.colorScheme = effectiveTheme(pref);
  try { localStorage.setItem(KEY, pref); } catch { /* private mode */ }
  window.dispatchEvent(new CustomEvent('decon:theme', { detail: { pref, theme: effectiveTheme(pref) } }));
}

/** Cycle system -> light -> dark -> system. */
export function nextTheme(pref = readTheme()) {
  return ORDER[(ORDER.indexOf(pref) + 1) % ORDER.length];
}

/** Call once at startup, before first paint, to avoid a flash of the wrong theme. */
export function installTheme() {
  applyTheme(readTheme());
  try {
    // follow the OS while the user has expressed no preference of their own
    window.matchMedia('(prefers-color-scheme: light)').addEventListener('change', () => {
      if (readTheme() === 'system') applyTheme('system');
    });
  } catch { /* older browsers */ }
}
